import React, { useState, useEffect } from 'react';
import './BrowseCategories.css';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedCategory, setSelectedCategory, selectLanguage, setSelectedBrand } from '../redux/cartSlice';
import { translations } from '../utils/translations';

const BrowseCategories = () => {
  const dispatch = useDispatch();
  const selectedCategory = useSelector(selectSelectedCategory);
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;
  const [startIndex, setStartIndex] = useState(0);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetch('/api/categories')
      .then((res) => res.json())
      .then((data) => {
        setCategories(data);
      })
      .catch((err) => {
        console.error('Error fetching categories:', err);
      });
  }, []);

  const handlePrev = () => {
    setStartIndex((prev) => (prev - 1 + categories.length) % categories.length);
  };

  const handleNext = () => {
    setStartIndex((prev) => (prev + 1) % categories.length);
  };

  const handleCategoryClick = (catName) => {
    const newCat = selectedCategory === catName ? 'All' : catName;
    dispatch(setSelectedCategory(newCat));
    dispatch(setSelectedBrand('All'));
    
    // Smooth scroll to explore-products
    const element = document.getElementById('explore-products');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getCategoryTranslation = (name) => {
    if (!name) return '';
    const lower = name.toLowerCase().trim();
    if (lower === 'mobile phone') return t.categoryMobilePhone || name;
    if (lower === 'accessories') return t.categoryAccessories || name;
    if (lower === 'gadget') return t.categoryGadget || name;
    if (lower === 'service' || lower === 'services') return t.categoryService || name;
    if (lower === 'parts') return t.categoryParts || name;
    if (lower === 'computers') return t.categoryComputers || name;
    if (lower === 'offers') return t.categoryOffers || name;
    if (lower === 'used phone') return t.categoryUsedPhone || name;
    if (lower === 'electronics') return t.categoryElectronics || name;
    return name;
  };

  // Get visible 6 categories
  const visibleCategories = [];
  if (categories && categories.length > 0) {
    for (let i = 0; i < Math.min(6, categories.length); i++) {
      visibleCategories.push(categories[(startIndex + i) % categories.length]);
    }
  }

  return (
    <section className="categories-section-wrapper">
      <div className="categories-section-container">
        {/* Section Title & Header */}
        <div className="section-header-block">
          <div className="section-label-container">
            <div className="label-badge"></div>
            <span className="label-text">{t.categoriesHeading}</span>
          </div>
          <div className="section-title-row">
            <h2 className="section-title">{t.browseByCategory}</h2>
            <div className="navigation-arrows">
              <button className="nav-arrow-btn" aria-label="Previous Category" onClick={handlePrev}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
              </button>
              <button className="nav-arrow-btn" aria-label="Next Category" onClick={handleNext}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Category Circle Cards Grid (6 Columns) */}
        <div className="categories-circle-grid animate-slide">
          {visibleCategories.map((cat) => {
            const catName = cat ? cat.name : '';
            const isActive = selectedCategory === catName;
            
            return (
              <div 
                key={cat._id || catName} 
                className={`category-circle-card ${isActive ? 'active' : ''}`}
                onClick={() => handleCategoryClick(catName)}
              >
                {cat && cat.brandLogo ? (
                  <div className="category-circle-wrapper brand-logo-wrapper" style={{ backgroundColor: '#ffffff', border: '1px solid #edf2f7', overflow: 'hidden' }}>
                    <img src={cat.brandLogo} alt={catName} className="category-brand-logo" style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                  </div>
                ) : cat && cat.icon && typeof cat.icon === 'string' && cat.icon.trim().startsWith('<svg') ? (
                  <div 
                    className="category-circle-wrapper"
                    dangerouslySetInnerHTML={{ __html: cat.icon }}
                  />
                ) : (
                  <div className="category-circle-wrapper">
                    {cat ? cat.icon : null}
                  </div>
                )}
                <span className="category-circle-name">{getCategoryTranslation(catName)}</span>
              </div>
            );
          })}
        </div>

        {/* Bottom divider line */}
        <div className="section-bottom-divider"></div>
      </div>
    </section>
  );
};

export default BrowseCategories;
